import { getUser } from "../utils/authService";
import { useState, useEffect } from "react";
import { FileText, Plus, DollarSign, CreditCard, Search, Eye, Trash2, Download, AlertCircle, CheckCircle, XCircle, Clock } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "../utils/apiClient";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const BillingManagement = () => {
  const [workOrders, setWorkOrders] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("workOrders");
  
  // Current user for approval permissions
  const currentUser = getUser() || {};
  const canApprove = ["Master Admin", "Management", "Project Director", "Project Manager"].includes(currentUser.role);
  
  // Work Order Dialog
  const [woDialogOpen, setWoDialogOpen] = useState(false);
  const [woForm, setWoForm] = useState({
    contractor_id: "",
    project_id: "",
    work_category: "",
    description: "",
    start_date: new Date().toISOString().split('T')[0],
    end_date: "",
    items: [{ description: "", unit: "Cum", quantity: 0, rate: 0, amount: 0 }],
    advance_paid: 0,
    terms_conditions: ""
  });

  // Invoice Dialog
  const [invDialogOpen, setInvDialogOpen] = useState(false);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
  const [invForm, setInvForm] = useState({
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: "",
    tax_percentage: 18,
    notes: ""
  });

  // Payment Dialog
  const [payDialogOpen, setPayDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [payForm, setPayForm] = useState({
    payment_date: new Date().toISOString().split('T')[0],
    amount: "",
    payment_mode: "Bank Transfer",
    reference_number: "",
    bank_name: "",
    remarks: ""
  });

  const WORK_CATEGORIES = ["Earthwork", "Concrete Work", "Steel/Reinforcement", "Road Work", "Drainage", "Electrical", "Plumbing", "Other"];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [woRes, invRes, payRes, conRes, projRes, statsRes] = await Promise.all([
        apiClient.get('/work-orders'),
        apiClient.get('/invoices'),
        apiClient.get('/payments'),
        apiClient.get('/contractors/active'),
        apiClient.get('/projects'),
        apiClient.get('/billing/stats')
      ]);
      setWorkOrders(woRes.data);
      setInvoices(invRes.data);
      setPayments(payRes.data);
      setContractors(conRes.data);
      setProjects(projRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  // Work Order Functions
  const addWoItem = () => {
    setWoForm(prev => ({
      ...prev,
      items: [...prev.items, { description: "", unit: "Cum", quantity: 0, rate: 0, amount: 0 }]
    }));
  };

  const updateWoItem = (index, field, value) => {
    setWoForm(prev => {
      const items = [...prev.items];
      items[index][field] = value;
      if (field === 'quantity' || field === 'rate') {
        items[index].amount = items[index].quantity * items[index].rate;
      }
      return { ...prev, items };
    });
  };

  const removeWoItem = (index) => {
    setWoForm(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const handleWoSubmit = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/work-orders', woForm);
      toast.success("Work order created");
      setWoDialogOpen(false);
      resetWoForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create work order");
    }
  };

  const resetWoForm = () => {
    setWoForm({
      contractor_id: "",
      project_id: "",
      work_category: "",
      description: "",
      start_date: new Date().toISOString().split('T')[0],
      end_date: "",
      items: [{ description: "", unit: "Cum", quantity: 0, rate: 0, amount: 0 }],
      advance_paid: 0,
      terms_conditions: ""
    });
  };

  const updateWoStatus = async (orderId, status) => {
    try {
      await apiClient.put(`/work-orders/${orderId}/status?status=${status}`);
      toast.success(`Work order ${status.toLowerCase()}`);
      fetchData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  // Invoice Functions
  const openInvoiceDialog = (workOrder) => {
    setSelectedWorkOrder(workOrder);
    setInvForm({
      invoice_date: new Date().toISOString().split('T')[0],
      due_date: new Date(Date.now() + 30*24*60*60*1000).toISOString().split('T')[0],
      tax_percentage: 18,
      notes: ""
    });
    setInvDialogOpen(true);
  };

  const handleInvSubmit = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/invoices', {
        work_order_id: selectedWorkOrder.id,
        ...invForm,
        items: selectedWorkOrder.items
      });
      toast.success("Invoice created");
      setInvDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create invoice");
    }
  };

  // Payment Functions
  const openPaymentDialog = (invoice) => {
    setSelectedInvoice(invoice);
    setPayForm({
      payment_date: new Date().toISOString().split('T')[0],
      amount: invoice.balance_due,
      payment_mode: "Bank Transfer",
      reference_number: "",
      bank_name: "",
      remarks: ""
    });
    setPayDialogOpen(true);
  };

  const handlePaySubmit = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/payments', {
        invoice_id: selectedInvoice.id,
        ...payForm,
        amount: parseFloat(payForm.amount)
      });
      toast.success("Payment recorded");
      setPayDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to record payment");
    }
  };

  // Invoice Approval Functions
  const handleApproveInvoice = async (invoiceId) => {
    try {
      await apiClient.put(`/invoices/${invoiceId}/approve`);
      toast.success("Invoice approved");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve invoice");
    }
  };

  const handleRejectInvoice = async (invoiceId) => {
    const reason = prompt("Enter rejection reason:");
    if (reason === null) return;
    
    try {
      await apiClient.put(`/invoices/${invoiceId}/reject?reason=${encodeURIComponent(reason)}`);
      toast.success("Invoice rejected");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reject invoice");
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      "Draft": "bg-slate-200 text-slate-600",
      "In Progress": "bg-primary/20 text-primary",
      "Completed": "bg-success/20 text-success",
      "Cancelled": "bg-error/20 text-error",
      "Pending": "bg-warning/20 text-warning",
      "Pending Approval": "bg-orange-100 text-orange-600",
      "Approved": "bg-blue-100 text-blue-600",
      "Rejected": "bg-error/20 text-error",
      "Partially Paid": "bg-accent/20 text-accent",
      "Paid": "bg-success/20 text-success"
    };
    return <span className={`px-2 py-1 rounded-sm text-xs font-medium ${config[status] || 'bg-slate-100'}`}>{status}</span>;
  };

  const getApprovalBadge = (approvalStatus) => {
    const config = {
      "Pending Approval": { bg: "bg-orange-100 text-orange-600", icon: Clock },
      "Approved": { bg: "bg-success/20 text-success", icon: CheckCircle },
      "Rejected": { bg: "bg-error/20 text-error", icon: XCircle }
    };
    const { bg, icon: Icon } = config[approvalStatus] || config["Pending Approval"];
    return (
      <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-sm text-xs font-medium ${bg}`}>
        <Icon className="w-3 h-3" />
        <span>{approvalStatus}</span>
      </span>
    );
  };

  const woTotal = woForm.items.reduce((sum, item) => sum + (item.amount || 0), 0);

  if (loading) return <div className="text-center py-12">Loading...</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="font-heading font-bold text-4xl text-primary tracking-tight">BILLING & WORK ORDERS</h1>
          <p className="text-slate-600 mt-2 font-body">Manage work orders, invoices, and payments</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid md:grid-cols-5 gap-4 mb-8">
        <div className="stat-card">
          <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">Work Orders</p>
          <p className="font-heading font-black text-3xl text-primary font-mono">{stats?.total_work_orders || 0}</p>
          <p className="text-sm text-slate-500 mt-1">₹{(stats?.work_orders_value || 0).toLocaleString()}</p>
        </div>
        <div className="stat-card">
          <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">Invoices</p>
          <p className="font-heading font-black text-3xl text-accent font-mono">{stats?.total_invoices || 0}</p>
          <p className="text-sm text-slate-500 mt-1">₹{(stats?.invoices_value || 0).toLocaleString()}</p>
        </div>
        <div className="stat-card border-l-4 border-l-orange-400">
          <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">Pending Approval</p>
          <p className="font-heading font-black text-3xl text-orange-500 font-mono">{stats?.pending_approval || 0}</p>
          <p className="text-sm text-slate-500 mt-1">invoices</p>
        </div>
        <div className="stat-card">
          <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">Payments</p>
          <p className="font-heading font-black text-3xl text-success font-mono">{stats?.total_payments || 0}</p>
          <p className="text-sm text-slate-500 mt-1">₹{(stats?.payments_value || 0).toLocaleString()}</p>
        </div>
        <div className="stat-card border-l-4 border-l-warning">
          <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">Pending Amount</p>
          <p className="font-heading font-black text-3xl text-warning font-mono">₹{(stats?.pending_amount || 0).toLocaleString()}</p>
          <p className="text-sm text-slate-500 mt-1">{stats?.pending_invoices || 0} invoices</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex space-x-4 mb-6 border-b border-slate-200">
        {[
          { id: "workOrders", label: "Work Orders", icon: FileText, count: workOrders.length },
          { id: "invoices", label: "Invoices", icon: DollarSign, count: invoices.length },
          { id: "payments", label: "Payments", icon: CreditCard, count: payments.length }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`pb-3 px-4 font-medium text-sm transition-colors flex items-center space-x-2 ${
              activeTab === tab.id ? "border-b-2 border-primary text-primary" : "text-slate-600 hover:text-primary"
            }`}
          >
            <tab.icon className="w-4 h-4" />
            <span>{tab.label} ({tab.count})</span>
          </button>
        ))}
      </div>

      {/* Work Orders Tab */}
      {activeTab === "workOrders" && (
        <div>
          <div className="flex justify-end mb-4">
            <Dialog open={woDialogOpen} onOpenChange={setWoDialogOpen}>
              <DialogTrigger asChild>
                <button onClick={resetWoForm} className="btn-primary flex items-center space-x-2">
                  <Plus className="w-4 h-4" />
                  <span>Create Work Order</span>
                </button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl">CREATE WORK ORDER</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleWoSubmit} className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Contractor *</label>
                      <select
                        required
                        value={woForm.contractor_id}
                        onChange={(e) => setWoForm({...woForm, contractor_id: e.target.value})}
                        className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        <option value="">Select Contractor</option>
                        {contractors.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Project *</label>
                      <select
                        required
                        value={woForm.project_id}
                        onChange={(e) => setWoForm({...woForm, project_id: e.target.value})}
                        className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        <option value="">Select Project</option>
                        {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Work Category *</label>
                      <select
                        required
                        value={woForm.work_category}
                        onChange={(e) => setWoForm({...woForm, work_category: e.target.value})}
                        className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        <option value="">Select</option>
                        {WORK_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Start Date *</label>
                      <input
                        type="date"
                        required
                        value={woForm.start_date}
                        onChange={(e) => setWoForm({...woForm, start_date: e.target.value})}
                        className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">End Date *</label>
                      <input
                        type="date"
                        required
                        value={woForm.end_date}
                        onChange={(e) => setWoForm({...woForm, end_date: e.target.value})}
                        className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Description *</label>
                    <input
                      type="text"
                      required
                      value={woForm.description}
                      onChange={(e) => setWoForm({...woForm, description: e.target.value})}
                      className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="Work order description"
                    />
                  </div>

                  {/* Line Items */}
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <label className="block text-xs font-medium text-slate-700 uppercase tracking-widest">Line Items</label>
                      <button type="button" onClick={addWoItem} className="text-primary text-sm hover:underline">+ Add Item</button>
                    </div>
                    <div className="border border-slate-200 rounded-sm overflow-hidden">
                      <table className="w-full">
                        <thead className="bg-slate-100">
                          <tr>
                            <th className="text-left py-2 px-3 text-xs uppercase">Description</th>
                            <th className="text-center py-2 px-3 text-xs uppercase w-20">Unit</th>
                            <th className="text-center py-2 px-3 text-xs uppercase w-20">Qty</th>
                            <th className="text-center py-2 px-3 text-xs uppercase w-24">Rate</th>
                            <th className="text-right py-2 px-3 text-xs uppercase w-28">Amount</th>
                            <th className="w-10"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {woForm.items.map((item, idx) => (
                            <tr key={idx} className="border-t border-slate-200">
                              <td className="py-2 px-2">
                                <input
                                  type="text"
                                  required
                                  value={item.description}
                                  onChange={(e) => updateWoItem(idx, 'description', e.target.value)}
                                  className="w-full px-2 py-1 border border-slate-300 rounded-sm text-sm"
                                  placeholder="Item description"
                                />
                              </td>
                              <td className="py-2 px-2">
                                <select
                                  value={item.unit}
                                  onChange={(e) => updateWoItem(idx, 'unit', e.target.value)}
                                  className="w-full px-2 py-1 border border-slate-300 rounded-sm text-sm"
                                >
                                  <option>Cum</option>
                                  <option>Sqm</option>
                                  <option>Rm</option>
                                  <option>MT</option>
                                  <option>Nos</option>
                                  <option>LS</option>
                                </select>
                              </td>
                              <td className="py-2 px-2">
                                <input
                                  type="number"
                                  value={item.quantity}
                                  onChange={(e) => updateWoItem(idx, 'quantity', parseFloat(e.target.value) || 0)}
                                  className="w-full px-2 py-1 border border-slate-300 rounded-sm text-sm font-mono text-center"
                                />
                              </td>
                              <td className="py-2 px-2">
                                <input
                                  type="number"
                                  value={item.rate}
                                  onChange={(e) => updateWoItem(idx, 'rate', parseFloat(e.target.value) || 0)}
                                  className="w-full px-2 py-1 border border-slate-300 rounded-sm text-sm font-mono text-center"
                                />
                              </td>
                              <td className="py-2 px-2 text-right font-mono text-sm font-semibold">
                                ₹{item.amount.toLocaleString()}
                              </td>
                              <td className="py-2 px-2">
                                {woForm.items.length > 1 && (
                                  <button type="button" onClick={() => removeWoItem(idx)} className="text-error hover:bg-red-100 p-1 rounded">
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="bg-slate-100">
                          <tr>
                            <td colSpan="4" className="py-2 px-3 text-right font-semibold">Total:</td>
                            <td className="py-2 px-3 text-right font-mono font-bold text-primary">₹{woTotal.toLocaleString()}</td>
                            <td></td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>

                  <div className="flex justify-end space-x-3 pt-4">
                    <button type="button" onClick={() => setWoDialogOpen(false)} className="btn-outline">Cancel</button>
                    <button type="submit" className="btn-primary">Create Work Order</button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="stat-card overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-slate-300">
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">WO Number</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Contractor</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Project</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Category</th>
                  <th className="text-right py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Amount</th>
                  <th className="text-center py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Status</th>
                  <th className="text-center py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {workOrders.map(wo => (
                  <tr key={wo.id} className="border-b border-slate-200 hover:bg-slate-50">
                    <td className="py-3 px-4 font-mono text-sm font-medium text-primary">{wo.work_order_number}</td>
                    <td className="py-3 px-4 text-sm">{wo.contractor_name}</td>
                    <td className="py-3 px-4 text-sm">{wo.project_name}</td>
                    <td className="py-3 px-4 text-sm">{wo.work_category}</td>
                    <td className="py-3 px-4 text-right font-mono text-sm font-semibold">₹{wo.total_amount?.toLocaleString()}</td>
                    <td className="py-3 px-4 text-center">{getStatusBadge(wo.status)}</td>
                    <td className="py-3 px-4 text-center">
                      <div className="flex items-center justify-center space-x-2">
                        {wo.status === "Draft" && (
                          <button onClick={() => updateWoStatus(wo.id, "In Progress")} className="text-xs text-primary hover:underline">Start</button>
                        )}
                        {wo.status === "In Progress" && (
                          <>
                            <button onClick={() => updateWoStatus(wo.id, "Completed")} className="text-xs text-success hover:underline">Complete</button>
                            <button onClick={() => openInvoiceDialog(wo)} className="text-xs text-accent hover:underline">Invoice</button>
                          </>
                        )}
                        {wo.status === "Completed" && (
                          <button onClick={() => openInvoiceDialog(wo)} className="text-xs text-accent hover:underline">Create Invoice</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {workOrders.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                <FileText className="w-16 h-16 mx-auto mb-4 text-slate-400" />
                No work orders yet.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Invoices Tab */}
      {activeTab === "invoices" && (
        <div className="stat-card overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b-2 border-slate-300">
                <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Invoice No.</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Contractor</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Date</th>
                <th className="text-right py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Total</th>
                <th className="text-right py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Paid</th>
                <th className="text-right py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Balance</th>
                <th className="text-center py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Approval</th>
                <th className="text-center py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Payment</th>
                <th className="text-center py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map(inv => (
                <tr key={inv.id} className={`border-b border-slate-200 hover:bg-slate-50 ${inv.approval_status === 'Rejected' ? 'bg-red-50' : ''}`}>
                  <td className="py-3 px-4 font-mono text-sm font-medium text-primary">{inv.invoice_number}</td>
                  <td className="py-3 px-4 text-sm">{inv.contractor_name}</td>
                  <td className="py-3 px-4 font-mono text-sm">{inv.invoice_date}</td>
                  <td className="py-3 px-4 text-right font-mono text-sm">₹{inv.total_amount?.toLocaleString()}</td>
                  <td className="py-3 px-4 text-right font-mono text-sm text-success">₹{inv.amount_paid?.toLocaleString()}</td>
                  <td className="py-3 px-4 text-right font-mono text-sm font-semibold text-warning">₹{inv.balance_due?.toLocaleString()}</td>
                  <td className="py-3 px-4 text-center">{getApprovalBadge(inv.approval_status || "Pending Approval")}</td>
                  <td className="py-3 px-4 text-center">{getStatusBadge(inv.status)}</td>
                  <td className="py-3 px-4 text-center">
                    <div className="flex items-center justify-center space-x-2">
                      {/* Approval Actions */}
                      {(inv.approval_status === "Pending Approval" || !inv.approval_status) && canApprove && (
                        <>
                          <button 
                            onClick={() => handleApproveInvoice(inv.id)} 
                            className="text-xs px-2 py-1 bg-success/20 text-success rounded hover:bg-success/30"
                            title="Approve"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </button>
                          <button 
                            onClick={() => handleRejectInvoice(inv.id)} 
                            className="text-xs px-2 py-1 bg-error/20 text-error rounded hover:bg-error/30"
                            title="Reject"
                          >
                            <XCircle className="w-4 h-4" />
                          </button>
                        </>
                      )}
                      {/* Payment Action - only if approved */}
                      {inv.approval_status === "Approved" && inv.status !== "Paid" && (
                        <button 
                          onClick={() => openPaymentDialog(inv)} 
                          className="text-xs px-2 py-1 bg-primary/20 text-primary rounded hover:bg-primary/30"
                        >
                          Pay
                        </button>
                      )}
                      {inv.status === "Paid" && (
                        <span className="text-xs text-success">Paid ✓</span>
                      )}
                    </div>
                    {/* Rejection reason */}
                    {inv.approval_status === "Rejected" && inv.rejection_reason && (
                      <p className="text-xs text-error mt-1">Reason: {inv.rejection_reason}</p>
                    )}
                    {/* Approved by info */}
                    {inv.approval_status === "Approved" && inv.approved_by_name && (
                      <p className="text-xs text-slate-500 mt-1">by {inv.approved_by_name}</p>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {invoices.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              <DollarSign className="w-16 h-16 mx-auto mb-4 text-slate-400" />
              No invoices yet. Create from completed work orders.
            </div>
          )}
        </div>
      )}

      {/* Payments Tab */}
      {activeTab === "payments" && (
        <div className="stat-card overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b-2 border-slate-300">
                <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Date</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Invoice</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Contractor</th>
                <th className="text-right py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Amount</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Mode</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Reference</th>
              </tr>
            </thead>
            <tbody>
              {payments.map(pay => (
                <tr key={pay.id} className="border-b border-slate-200 hover:bg-slate-50">
                  <td className="py-3 px-4 font-mono text-sm">{pay.payment_date}</td>
                  <td className="py-3 px-4 font-mono text-sm text-primary">{pay.invoice_number}</td>
                  <td className="py-3 px-4 text-sm">{pay.contractor_name}</td>
                  <td className="py-3 px-4 text-right font-mono text-sm font-semibold text-success">₹{pay.amount?.toLocaleString()}</td>
                  <td className="py-3 px-4 text-sm">{pay.payment_mode}</td>
                  <td className="py-3 px-4 text-sm font-mono">{pay.reference_number || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {payments.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              <CreditCard className="w-16 h-16 mx-auto mb-4 text-slate-400" />
              No payments recorded yet.
            </div>
          )}
        </div>
      )}

      {/* Invoice Dialog */}
      <Dialog open={invDialogOpen} onOpenChange={setInvDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">CREATE INVOICE</DialogTitle>
          </DialogHeader>
          {selectedWorkOrder && (
            <form onSubmit={handleInvSubmit} className="space-y-4 mt-4">
              <p className="text-sm text-slate-600">Creating invoice for: <strong>{selectedWorkOrder.work_order_number}</strong></p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Invoice Date</label>
                  <input
                    type="date"
                    required
                    value={invForm.invoice_date}
                    onChange={(e) => setInvForm({...invForm, invoice_date: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-300 rounded-sm font-mono"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Due Date</label>
                  <input
                    type="date"
                    required
                    value={invForm.due_date}
                    onChange={(e) => setInvForm({...invForm, due_date: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-300 rounded-sm font-mono"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">GST %</label>
                <input
                  type="number"
                  value={invForm.tax_percentage}
                  onChange={(e) => setInvForm({...invForm, tax_percentage: parseFloat(e.target.value)})}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm font-mono"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button type="button" onClick={() => setInvDialogOpen(false)} className="btn-outline">Cancel</button>
                <button type="submit" className="btn-primary">Create Invoice</button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={payDialogOpen} onOpenChange={setPayDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">RECORD PAYMENT</DialogTitle>
          </DialogHeader>
          {selectedInvoice && (
            <form onSubmit={handlePaySubmit} className="space-y-4 mt-4">
              <p className="text-sm text-slate-600">
                Invoice: <strong>{selectedInvoice.invoice_number}</strong> | 
                Balance: <strong className="text-warning">₹{selectedInvoice.balance_due?.toLocaleString()}</strong>
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Payment Date</label>
                  <input
                    type="date"
                    required
                    value={payForm.payment_date}
                    onChange={(e) => setPayForm({...payForm, payment_date: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-300 rounded-sm font-mono"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Amount</label>
                  <input
                    type="number"
                    required
                    value={payForm.amount}
                    onChange={(e) => setPayForm({...payForm, amount: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-300 rounded-sm font-mono"
                    max={selectedInvoice.balance_due}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Payment Mode</label>
                  <select
                    value={payForm.payment_mode}
                    onChange={(e) => setPayForm({...payForm, payment_mode: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-300 rounded-sm"
                  >
                    <option>Bank Transfer</option>
                    <option>Cheque</option>
                    <option>Cash</option>
                    <option>UPI</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Reference No.</label>
                  <input
                    type="text"
                    value={payForm.reference_number}
                    onChange={(e) => setPayForm({...payForm, reference_number: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-300 rounded-sm font-mono"
                    placeholder="Transaction/Cheque No."
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button type="button" onClick={() => setPayDialogOpen(false)} className="btn-outline">Cancel</button>
                <button type="submit" className="btn-primary">Record Payment</button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BillingManagement;
